import React, { useCallback, useRef } from 'react';
const { v4: uuidv4 } = require('uuid');

const StandardTideCurves = () => {
  const svgRef = useRef(null);
  const verticalLineRef = useRef(null);
  const horizontalLineRef = useRef(null);
  const frozenRef = useRef(false);

  const height = 800;
  const width = 1600;

  const marginLeft = 110;
  const marginRight = 110;
  const marginTop = 55;
  const marginBottom = 150;

  const viewBox = `${-marginLeft} ${-marginTop} ${width + marginLeft + marginRight} ${height + marginTop + marginBottom}`;
  const maxRange = 3.0;

  const handleClick = useCallback(() => {
    frozenRef.current = !frozenRef.current;
    if (svgRef.current) {
      svgRef.current.style.cursor = frozenRef.current ? 'default' : 'crosshair';
    }
  }, []);

  // Direct DOM update — no React re-render on every mouse move
  const handleMouseMove = useCallback((e) => {
    if (frozenRef.current) return;
    const svgEl = svgRef.current;
    if (!svgEl) return;
    const point = svgEl.createSVGPoint();
    point.x = e.clientX;
    point.y = e.clientY;
    const ctm = svgEl.getScreenCTM();
    if (!ctm) return;
    const { x, y } = point.matrixTransform(ctm.inverse());
    if (verticalLineRef.current) {
      const cx = Math.max(0, Math.min(width, x));
      verticalLineRef.current.setAttribute('x1', cx);
      verticalLineRef.current.setAttribute('x2', cx);
    }
    if (horizontalLineRef.current) {
      const cy = Math.max(0, Math.min(height, y));
      horizontalLineRef.current.setAttribute('y1', cy);
      horizontalLineRef.current.setAttribute('y2', cy);
    }
  }, []);

  function makeGrid() {
    const lines = [];
    const textLines = [];

    const dividerX = 12;
    const segmentWidth = width / dividerX;

    for (let i = 0; i <= dividerX; i += 1) {
      lines.push(
        <line key={`vl${i}`} x1={i * segmentWidth} x2={i * segmentWidth} y1={0} y2={height} style={{ stroke: 'lightgray', strokeWidth: 2 }} />
      );
      textLines.push(
        <text key={`vt1${i}`} style={{ fill: 'black', fontSize: 14 }} textAnchor="middle" x={i * segmentWidth} y={-10}>{i - 6}</text>
      );
      textLines.push(
        <text key={`vt2${i}`} style={{ fill: 'black', fontSize: 14 }} textAnchor="middle" x={i * segmentWidth} y={height + 20}>{i - 6}</text>
      );
    }

    for (let i = 0; i <= dividerX; i += 1) {
      const x = (i * segmentWidth) + (segmentWidth / 2);
      lines.push(
        <line key={`dvl${i}`} x1={x} x2={x} y1={0} y2={height} style={{ stroke: 'lightgray', strokeWidth: 2, strokeDasharray: 8 }} />
      );
    }

    const dividerY = 30;
    const segmentHeight = height / dividerY;
    for (let i = 0; i <= dividerY; i += 1) {
      lines.push(
        <line key={`hl${i}`} x1={0} x2={width} y1={i * segmentHeight} y2={i * segmentHeight} style={{ stroke: 'lightgray', strokeWidth: 2 }} />
      );
      const label = (3 - (i / 10)).toFixed(1);
      textLines.push(
        <text key={`hr${i}`} style={{ fill: 'black', fontSize: 14 }} textAnchor="start" x={width + 8} y={(i * segmentHeight) + 5}>{label}</text>
      );
      textLines.push(
        <text key={`hl2${i}`} style={{ fill: 'black', fontSize: 14 }} textAnchor="end" x={-8} y={(i * segmentHeight) + 5}>{label}</text>
      );
    }

    return <>{lines}{textLines}</>;
  }

  function makePath(points, range) {
    const d = `M ${points.join(' ')} `;
    const pathId = uuidv4();
    return (
      <>
        <path id={pathId} d={d} stroke="#0066cc" strokeWidth="2" fill="none" />
        <text style={{ fill: 'black', fontSize: 18 }} dy={10}>
          <textPath href={`#${pathId}`} startOffset="50%" textAnchor="middle" textLength="160">{range}m Range</textPath>
        </text>
      </>
    );
  }

  function plotOnBell(x, scale) {
    scale = scale || false;
    const stdD = 0.16;
    const mean = 0.5;
    if (scale) {
      return 1 / ((1 / (stdD * Math.sqrt(2 * Math.PI))) * Math.pow(Math.E, -1 * Math.pow(x - mean, 2) / (2 * Math.pow(stdD, 2))));
    }
    return ((1 / (stdD * Math.sqrt(2 * Math.PI))) * Math.pow(Math.E, -1 * Math.pow(x - mean, 2) / (2 * Math.pow(stdD, 2)))) * plotOnBell(0.5, true);
  }

  const renderBell = (increment, range) => {
    const shapePoints = [];
    for (let i = 1; i < width; i += 1) {
      const plot = plotOnBell(i / width);
      shapePoints.push([[i, height - (increment * plot)]]);
    }
    return makePath(shapePoints, range);
  };

  return (
    <div>
      <svg
        ref={svgRef}
        onMouseMove={handleMouseMove}
        onClick={handleClick}
        style={{ width: '100%', height: 'auto', display: 'block', cursor: 'crosshair' }}
        version="1.1"
        viewBox={viewBox}
      >
        {makeGrid()}

        {/* Crosshair — updated via direct DOM ref, zero re-renders */}
        <line ref={verticalLineRef} x1={0} x2={0} y1={0} y2={height} style={{ stroke: 'red', strokeWidth: 2, strokeDasharray: 8, pointerEvents: 'none' }} />
        <line ref={horizontalLineRef} x1={0} x2={width} y1={0} y2={0} style={{ stroke: 'orange', strokeWidth: 2, strokeDasharray: 8, pointerEvents: 'none' }} />

        {renderBell(height, maxRange.toFixed(1))}
        {renderBell(height * 0.832, (maxRange * 0.832).toFixed(1))}
        {renderBell(height * 0.665, (maxRange * 0.665).toFixed(1))}
        {renderBell(height * 0.5, (maxRange * 0.5).toFixed(1))}
        {renderBell(height * 0.332, (maxRange * 0.332).toFixed(1))}
        {renderBell(height * 0.165, (maxRange * 0.165).toFixed(1))}

        {/* Top Text */}
        <text x={width / 2} y={-32} textAnchor="middle" style={{ fill: 'black', fontSize: 18 }}>
          Hourly interval from nearest high water
        </text>

        {/* Bottom Text */}
        <text x={width / 2} y={height + 50} textAnchor="middle" style={{ fill: 'black', fontSize: 20, fontWeight: 'bold' }}>
          Hourly interval from nearest high water
        </text>
        <text x={width / 2} y={height + 82} textAnchor="middle" style={{ fill: 'blue', fontSize: 16 }}>
          <tspan x={width / 2} dy="0">In an effort to mimic the curve as you may use it in the tide book, the cursor on the curve does not return height/hours as you move the crosshairs.</tspan>
          <tspan x={width / 2} dy="22">Instead you will need to derive these visually from the chart. The crosshair lines are just mimicking ruler lines.</tspan>
          <tspan x={width / 2} dy="22">Please note that you can click to freeze the cross hairs on the curve. Click again and it unfreezes.</tspan>
        </text>

        {/* Left Text */}
        <text
          transform={`translate(${-marginLeft + 22}, ${height / 2}) rotate(-90)`}
          textAnchor="middle"
          style={{ fill: 'black', fontSize: 18 }}
        >
          Height to be added to the height of low water
        </text>

        {/* Right Text */}
        <text
          transform={`translate(${width + marginRight - 22}, ${height / 2}) rotate(90)`}
          textAnchor="middle"
          style={{ fill: 'black', fontSize: 18 }}
        >
          Range of tide in metres
        </text>
      </svg>
    </div>
  );
};

export default StandardTideCurves;
